import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Form, Formik } from "formik";
import { t } from "ttag";
import { Button, ChevronLeft, IconButton, PhoneInput, Select, TextField, Typography } from "@coolremote/shared";
import { useStoreActions, useStoreState } from "../../../models/RootStore";
import { createValidationSchema } from "../../../components/UnifiedAcceptInvite/validationSchemas";
import { useUserPreferences } from "../../../components/UnifiedAcceptInvite/hooks/useUserPreferences";
import { isValidDeviceRegistrationState } from "../deviceRegistrationLocationState";
import { isQaBypassCredentials } from "../../../models/devicePublicRegistrationTestBypass";
import { NewUserFlowState, PersonalDetailsValues } from "./newUserFlowState";
import { isUsernameAvailable } from "./deviceRegistrationPublicApi";
import { getQaBypassPersonalDetails } from "./qaBypassMockData";
import parentStyles from "../DeviceRegistrationPublic.module.css";
import styles from "./NewUserFlow.module.css";

interface PreferenceOption {
  value: number;
  label: string;
}

interface PreferenceLists {
  temperatureScaleOptions: PreferenceOption[];
  timeFormatOptions: PreferenceOption[];
  dateFormatOptions: PreferenceOption[];
}

const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyValues: PersonalDetailsValues = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  temperatureScale: "",
  timeFormat: "",
  dateFormat: "",
  password: "",
  confirmPassword: ""
};

/**
 * Step 1 of the new-user branch (CM-618): personal details + preferences,
 * reusing the validation rules and preference lists from the existing
 * "accept invite as a new user" flow (`UnifiedAcceptInvite`).
 */
const PersonalDetailsStep: React.FC = () => {
  const history = useHistory<NewUserFlowState | undefined>();
  const deviceState = history.location.state;

  const types = useStoreState((s) => s.types);
  const getTypes = useStoreActions((actions) => actions.getTypes);
  const { lists } = useUserPreferences() as { lists: PreferenceLists };

  useEffect(() => {
    if (!isValidDeviceRegistrationState(deviceState)) {
      history.replace("/register-device");
      return;
    }
    // `types` is normally loaded post-login; this flow runs pre-login, so
    // fetch it explicitly the same way the invite-accept flow does.
    if (!types?.timeFormat) {
      getTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isValidDeviceRegistrationState(deviceState)) {
    return null;
  }

  const handleBack = () => {
    history.goBack();
  };

  const handleSubmit = (values: PersonalDetailsValues) => {
    const nextState = { ...deviceState, personalDetails: values } as NewUserFlowState;
    // Attach the typed values to *this* history entry (not just the next one),
    // so that going back from Customer Name lands here with the form restored
    // instead of blank — same convention as DeviceRegistrationWizard's handleSubmit.
    history.replace(history.location.pathname, nextState);
    history.push("/register-device/new-user/customer-name", nextState);
  };

  const isQaBypass = isQaBypassCredentials(deviceState.serial, deviceState.pin);

  // Local format check first, then the real dry-check — informational only
  // (the final submit always re-checks for real), so anything that isn't
  // email-shaped yet just resolves as valid rather than round-tripping.
  const validateEmailAsync = async (email: string | number): Promise<string | undefined> => {
    if (typeof email !== "string" || !EMAIL_FORMAT.test(email)) {
      return undefined;
    }
    try {
      const available = await isUsernameAvailable(email);
      return available ? undefined : t`This email is already registered`;
    } catch (err) {
      const sdkError = err as { status?: number; errorCode?: string };
      if (sdkError?.status === 400 && sdkError?.errorCode === "VALIDATION_ERROR") {
        return t`Please enter a valid email address`;
      }
      // Network hiccup or the endpoint not being live — never block on this,
      // the final submit always re-checks for real.
      return undefined;
    }
  };

  // The shared Select renders its first option's label by default even when no
  // value has actually been chosen (it's a display default, not a real
  // selection) — so leaving these unset makes the field look pre-filled while
  // Formik still treats it as empty and required, and REGISTER silently
  // refuses to submit. Default the actual value to that same first option so
  // what's shown matches what's selected, for every user, not only QA bypass.
  const getInitialValues = (): PersonalDetailsValues => {
    const preferenceDefaults = {
      temperatureScale: lists.temperatureScaleOptions[0]?.value ?? "",
      timeFormat: lists.timeFormatOptions[0]?.value ?? "",
      dateFormat: lists.dateFormatOptions[0]?.value ?? ""
    };
    if (deviceState.personalDetails) {
      return { ...preferenceDefaults, ...deviceState.personalDetails };
    }
    if (isQaBypass) {
      return { ...getQaBypassPersonalDetails(), ...preferenceDefaults };
    }
    return { ...emptyValues, ...preferenceDefaults };
  };

  return (
    <div className={parentStyles.screen}>
      <div className={parentStyles.appBar}>
        <IconButton className={parentStyles.backButton} onClick={handleBack} aria-label={t`Back`}>
          <ChevronLeft width={20} height={20} />
        </IconButton>
        <span className={parentStyles.appBarTitle}>{t`Personal Details`}</span>
        <span />
      </div>

      <div className={styles.stepContent}>
        <Formik
          initialValues={getInitialValues()}
          enableReinitialize
          validationSchema={createValidationSchema()}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form className={styles.form}>
              <TextField
                label={`${t`First Name`} *`}
                fullWidth
                value={values.firstName}
                onChange={(v: string) => setFieldValue("firstName", v)}
                error={touched.firstName ? errors.firstName : undefined}
              />
              <TextField
                label={`${t`Last Name`} *`}
                fullWidth
                value={values.lastName}
                onChange={(v: string) => setFieldValue("lastName", v)}
                error={touched.lastName ? errors.lastName : undefined}
              />
              <PhoneInput
                label={t`Phone Number`}
                fullWidth
                defaultCountry="us"
                disableAreaCodes
                className={styles.phoneField}
                value={values.phone}
                onChange={(v: string) => setFieldValue("phone", v)}
                error={!!(errors.phone && touched.phone)}
              />
              {touched.phone && errors.phone && (
                <Typography variant="caption" color="error">
                  {errors.phone}
                </Typography>
              )}
              <TextField
                label={`${t`Email`} *`}
                fullWidth
                type="email"
                autoComplete="off"
                value={values.email}
                onChange={(v: string) => setFieldValue("email", v)}
                error={touched.email ? errors.email : undefined}
                asyncValidate={validateEmailAsync}
              />
              <Select
                label={`${t`Temperature Units`} *`}
                fullWidth
                value={values.temperatureScale}
                onChange={(v: number) => setFieldValue("temperatureScale", v)}
                options={lists.temperatureScaleOptions}
                error={!!(errors.temperatureScale && touched.temperatureScale)}
              />
              {touched.temperatureScale && errors.temperatureScale && (
                <Typography variant="caption" color="error">
                  {errors.temperatureScale}
                </Typography>
              )}
              <Select
                label={`${t`Time Format`} *`}
                fullWidth
                value={values.timeFormat}
                onChange={(v: number) => setFieldValue("timeFormat", v)}
                options={lists.timeFormatOptions}
                error={!!(errors.timeFormat && touched.timeFormat)}
              />
              {touched.timeFormat && errors.timeFormat && (
                <Typography variant="caption" color="error">
                  {errors.timeFormat}
                </Typography>
              )}
              <Select
                label={`${t`Date Format`} *`}
                fullWidth
                value={values.dateFormat}
                onChange={(v: number) => setFieldValue("dateFormat", v)}
                options={lists.dateFormatOptions}
                error={!!(errors.dateFormat && touched.dateFormat)}
              />
              {touched.dateFormat && errors.dateFormat && (
                <Typography variant="caption" color="error">
                  {errors.dateFormat}
                </Typography>
              )}
              <TextField
                label={`${t`Password`} *`}
                fullWidth
                type="password"
                autoComplete="new-password"
                value={values.password}
                onChange={(v: string) => setFieldValue("password", v)}
                error={touched.password ? errors.password : undefined}
              />
              <TextField
                label={`${t`Confirm Password`} *`}
                fullWidth
                type="password"
                autoComplete="new-password"
                value={values.confirmPassword}
                onChange={(v: string) => setFieldValue("confirmPassword", v)}
                error={touched.confirmPassword ? errors.confirmPassword : undefined}
              />
              <Button type="submit" variant="contained" className={styles.primaryButton}>
                {t`Next`}
              </Button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default PersonalDetailsStep;
